/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useState, type ChangeEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type ClinicListItem } from '../../lib/api/sa.js';
import { useSaClinics } from '../../lib/queries/sa.js';
import { uiStore } from '../../lib/stores/ui-store.js';

import { ClinicTableRow } from './clinic-row.js';
import { OnboardingWizard } from './onboarding-wizard.js';

const Search = observer(function Search(): JSX.Element {
  const { t } = useTranslation();
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value);
  }, []);
  return (
    <div className="mb-4">
      <input
        type="search"
        value={uiStore.search}
        onChange={onSearch}
        placeholder={t('sa.clinics.searchPlaceholder')}
        aria-label={t('sa.clinics.searchPlaceholder')}
        className="focus-ring h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      />
    </div>
  );
});

const HEAD_CLASS =
  'border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400';

function ClinicsTable({
  rows,
  onOpen,
}: {
  readonly rows: readonly ClinicListItem[];
  readonly onOpen: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-left text-sm">
        <thead className={HEAD_CLASS}>
          <tr>
            <th className="px-5 py-3 font-semibold">{t('sa.clinics.colName')}</th>
            <th className="px-5 py-3 font-semibold">{t('sa.clinics.colStatus')}</th>
            <th className="px-5 py-3 font-semibold">{t('sa.clinics.colPlan')}</th>
            <th className="px-5 py-3 font-semibold">{t('sa.clinics.colMrr')}</th>
            <th className="px-5 py-3 font-semibold">{t('sa.clinics.colTherapists')}</th>
            <th className="px-5 py-3 font-semibold">{t('sa.clinics.colActiveClients')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((clinic) => (
            <ClinicTableRow key={clinic.id} clinic={clinic} onOpen={onOpen} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Super Admin → Clinics. Searchable tenant list + onboarding wizard. */
export const SaClinicsPage = observer(function SaClinicsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clinics = useSaClinics();
  const [open, setOpen] = useState(false);

  const openWizard = useCallback(() => {
    setOpen(true);
  }, []);
  const closeWizard = useCallback(() => {
    setOpen(false);
  }, []);
  const onOpen = useCallback(
    (id: string) => {
      void navigate(`/sa/clinics/${id}`);
    },
    [navigate],
  );

  const q = uiStore.search.trim().toLowerCase();
  const all = clinics.data ?? [];
  const rows =
    q === ''
      ? all
      : all.filter((c) =>
          [c.name, c.planName ?? '', c.status].some((v) => v.toLowerCase().includes(q)),
        );

  return (
    <div className="p-8">
      <PageHeader
        title={t('sa.clinics.title')}
        subtitle={t('sa.clinics.count', { count: all.length })}
        action={
          <Button onClick={openWizard}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('sa.clinics.add')}
          </Button>
        }
      />
      <Search />

      <QueryState
        isLoading={clinics.isLoading}
        isError={clinics.isError}
        isEmpty={all.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('sa.clinics.empty')}
      >
        <ClinicsTable rows={rows} onOpen={onOpen} />
      </QueryState>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('sa.wizard.title')}
        description={t('sa.wizard.subtitle')}
        closeLabel={t('common.cancel')}
      >
        <OnboardingWizard onDone={closeWizard} />
      </Modal>
    </div>
  );
});
